import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import CloseThin from '@wix/da-ds/pkg/Icons/generated/components/CloseThin';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import { BiData } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import s from './SearchModal.scss';
const USERNAME_REGEX = /^[A-Za-z0-9][A-Za-z0-9-]{1,18}[A-Za-z0-9]$/;
const SearchModal = ({ onClose, searchQuery, isLegacyHeader })=>{
    const { t } = useTranslation();
    const isMobile = useContext(MobileContext);
    const [inputValue, setInputValue] = useState(searchQuery !== null && searchQuery !== void 0 ? searchQuery : '');
    const logBiEvent = useBiLogger();
    const searchInputRef = useRef(null);
    const handleChange = useCallback((value)=>{
        setInputValue(value);
    }, []);
    const handleSubmit = useCallback((e)=>{
        var _searchInputRef_current_value, _searchInputRef_current;
        e.preventDefault();
        const searchTerm = (_searchInputRef_current = searchInputRef.current) === null || _searchInputRef_current === void 0 ? void 0 : (_searchInputRef_current_value = _searchInputRef_current.value) === null || _searchInputRef_current_value === void 0 ? void 0 : _searchInputRef_current_value.trim();
        if (!searchTerm) {
            return; // dont allow empty search
        }
        const searchUrl = Url.searchLink(searchTerm);
        const username = searchTerm.slice(1);
        const isCorrectUsername = searchTerm.startsWith('@') && USERNAME_REGEX.test(username);
        logBiEvent(BiData({
            evid: 710,
            search_terms: searchTerm,
            sectionname: 'popup'
        }));
        // Only search usernames if we are not on subdomain!
        if (isCorrectUsername && !isLegacyHeader) {
            const userLinkUrl = Url.userLink(username);
            // to test this locally do `.replace('.com', '.lan')`
            const http = new XMLHttpRequest();
            http.open('HEAD', userLinkUrl);
            http.onreadystatechange = function() {
                if (this.readyState === this.DONE) {
                    const doesUserExists = this.status === 200;
                    const redirectUrl = doesUserExists ? userLinkUrl : searchUrl;
                    window.location.assign(redirectUrl);
                }
            };
            http.send();
        } else {
            window.location.assign(searchUrl);
        }
    }, [
        isLegacyHeader,
        logBiEvent
    ]);
    const handleInputKeyDown = useCallback((event)=>{
        if (event.key === 'Enter' || event.key === 'Return') {
            event.preventDefault();
            event.stopPropagation();
            handleSubmit(event);
        }
    }, [
        handleSubmit
    ]);
    useEffect(()=>{
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);
    return /*#__PURE__*/ React.createElement(Modal, {
        overlayClassName: s['search-popup'],
        className: classnames(s['search-popup-content'], s['updated-inputs']),
        shouldCloseOnOverlayClick: true,
        shouldCloseOnEsc: true,
        onRequestClose: onClose,
        isOpen: true
    }, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("div", {
        className: s['search-popup-form'],
        onClick: (e)=>e.stopPropagation()
    }, /*#__PURE__*/ React.createElement("form", {
        className: s['search-form'],
        action: Url.searchLink(),
        onSubmit: handleSubmit
    }, /*#__PURE__*/ React.createElement(TextInput, {
        ref: searchInputRef,
        id: "search-input",
        label: t('siteHeader.searchBar.placeholder'),
        placeholder: t('siteHeader.searchBar.placeholder'),
        value: inputValue,
        onChange: (e)=>handleChange(e.target.value),
        onKeyDown: handleInputKeyDown,
        postfix: /*#__PURE__*/ React.createElement(PlainButtonOrLink, {
            onClick: handleSubmit,
            biData: inputValue.length === 0 ? BiData({
                evid: 700,
                link_name: 'search'
            }) : undefined
        }, /*#__PURE__*/ React.createElement(SearchIcon, {
            disableDefaultColors: true
        })),
        className: s['search-popup-input'],
        noBottomSpacing: true,
        isLabelHidden: true,
        autoFocus: true,
        biData: BiData({
            evid: 700,
            link_name: 'search'
        })
    })), /*#__PURE__*/ React.createElement("button", {
        className: s['search-popup-close-button'],
        onClick: onClose
    }, /*#__PURE__*/ React.createElement(CloseThin, null))), isMobile && /*#__PURE__*/ React.createElement("div", {
        className: s['search-suggestions']
    }, /*#__PURE__*/ React.createElement("h3", null, t('siteHeader.search.suggestions')), [
        'Photography',
        'Body Art',
        'Artisan Designers',
        'Fashion',
        'Literature'
    ].map((i)=>/*#__PURE__*/ React.createElement("a", {
            key: i,
            href: Url.searchLink(i.toLowerCase())
        }, i)))));
};
SearchModal.displayName = 'SearchModal';
export default SearchModal;
